import React, { useState, useEffect } from 'react';
import { Building2, Users, CreditCard, DollarSign, TrendingUp, TrendingDown, AlertTriangle, Clock, Minus } from 'lucide-react';
import { supabase } from '../supabase';
import { formatCurrency, formatDate, getMonthlyRevenue } from '../utils/commissionCalc';
import { logAdminAction } from '../utils/auditLog';

export default function Overview({ navigateToCompany }) {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalCompanies: 0,
    totalUsers: 0,
    activeSubscriptions: 0,
    mrr: 0
  });
  const [trends, setTrends] = useState({
    newCompaniesThisWeek: 0,
    newCompaniesPrevWeek: 0,
    newUsersThisWeek: 0,
    newUsersPrevWeek: 0,
    newSubsThisWeek: 0,
    newSubsPrevWeek: 0,
    prevMrr: 0
  });
  const [trialFunnel, setTrialFunnel] = useState({
    trialsStarted30d: 0,
    converted30d: 0,
    conversionRate: 0
  });
  const [recentCompanies, setRecentCompanies] = useState([]);
  const [recentUsers, setRecentUsers] = useState([]);
  const [expiredTrials, setExpiredTrials] = useState([]);
  const [selectedTrials, setSelectedTrials] = useState(new Set());
  const [bulkLoading, setBulkLoading] = useState(false);
  const [inactiveCompanies, setInactiveCompanies] = useState([]);
  const [activeTrialsCount, setActiveTrialsCount] = useState(0);
  const [oldTrialsCount, setOldTrialsCount] = useState(0);
  const [oldTrials, setOldTrials] = useState([]);

  useEffect(() => {
    loadOverviewData();
  }, []);

  const loadOverviewData = async () => {
    try {
      // Fetch all data in parallel
      const [companiesRes, usersRes, userCompaniesRes] = await Promise.all([
        supabase
          .from('companies')
          .select('id, name, created_at, subscription_status, tier, billing_interval, trial_ends_at, stripe_subscription_id, is_demo')
          .order('created_at', { ascending: false }),
        supabase
          .from('user_profiles')
          .select('id, full_name, email, created_at')
          .order('created_at', { ascending: false }),
        supabase
          .from('user_companies')
          .select('user_id, company_id, role')
      ]);

      const companies = companiesRes.data || [];
      const users = usersRes.data || [];
      const userCompanies = userCompaniesRes.data || [];

      // Build company lookup for user enrichment
      const companyMap = {};
      companies.forEach(c => { companyMap[c.id] = c; });

      // Build owner lookup (role = 'owner')
      const ownerMap = {};
      const userMap = {};
      users.forEach(u => { userMap[u.id] = u; });
      userCompanies.forEach(uc => {
        if (uc.role === 'owner') {
          ownerMap[uc.company_id] = userMap[uc.user_id];
        }
      });

      // Stats
      const activeCompanies = companies.filter(c => c.subscription_status === 'active');
      let mrr = 0;
      activeCompanies.forEach(c => {
        mrr += getMonthlyRevenue(c.tier, c.billing_interval);
      });

      setStats({
        totalCompanies: companies.length,
        totalUsers: users.length,
        activeSubscriptions: activeCompanies.length,
        mrr
      });

      // Trend calculations
      const now = new Date();
      const sevenDaysAgo = new Date(now);
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
      const fourteenDaysAgo = new Date(now);
      fourteenDaysAgo.setDate(fourteenDaysAgo.getDate() - 14);
      const thirtyDaysAgoTrend = new Date(now);
      thirtyDaysAgoTrend.setDate(thirtyDaysAgoTrend.getDate() - 30);

      const newCompaniesThisWeek = companies.filter(c => new Date(c.created_at) >= sevenDaysAgo).length;
      const newCompaniesPrevWeek = companies.filter(c => {
        const d = new Date(c.created_at);
        return d >= fourteenDaysAgo && d < sevenDaysAgo;
      }).length;

      const newUsersThisWeek = users.filter(u => new Date(u.created_at) >= sevenDaysAgo).length;
      const newUsersPrevWeek = users.filter(u => {
        const d = new Date(u.created_at);
        return d >= fourteenDaysAgo && d < sevenDaysAgo;
      }).length;

      const newSubsThisWeek = companies.filter(c =>
        c.subscription_status === 'active' && new Date(c.created_at) >= sevenDaysAgo
      ).length;
      const newSubsPrevWeek = companies.filter(c => {
        const d = new Date(c.created_at);
        return c.subscription_status === 'active' && d >= fourteenDaysAgo && d < sevenDaysAgo;
      }).length;

      // Estimate previous MRR by excluding companies created in last 30 days that are active
      const recentActiveCompanies = companies.filter(c =>
        c.subscription_status === 'active' && new Date(c.created_at) >= thirtyDaysAgoTrend
      );
      let recentMrr = 0;
      recentActiveCompanies.forEach(c => {
        recentMrr += getMonthlyRevenue(c.tier, c.billing_interval);
      });
      const prevMrr = mrr - recentMrr;

      setTrends({
        newCompaniesThisWeek,
        newCompaniesPrevWeek,
        newUsersThisWeek,
        newUsersPrevWeek,
        newSubsThisWeek,
        newSubsPrevWeek,
        prevMrr
      });

      // Trial conversion funnel (last 30 days) — exclude demo/sandbox accounts
      const companiesLast30d = companies.filter(c =>
        !c.is_demo && new Date(c.created_at) >= thirtyDaysAgoTrend
      );
      const trialsStarted30d = companiesLast30d.filter(c =>
        c.subscription_status === 'trialing' || c.subscription_status === 'active'
      ).length;
      const converted30d = companiesLast30d.filter(c =>
        c.subscription_status === 'active' && c.stripe_subscription_id
      ).length;
      const conversionRate = trialsStarted30d > 0 ? (converted30d / trialsStarted30d) * 100 : 0;

      setTrialFunnel({ trialsStarted30d, converted30d, conversionRate });

      // Recent 10 companies with owner info
      setRecentCompanies(companies.slice(0, 10).map(c => ({
        ...c,
        owner: ownerMap[c.id] || null
      })));

      // Recent 10 users with company info
      const recentUsersList = users.slice(0, 10).map(u => {
        const uc = userCompanies.find(uc => uc.user_id === u.id);
        return {
          ...u,
          company: uc ? companyMap[uc.company_id] : null
        };
      });
      setRecentUsers(recentUsersList);

      // Expired trials — non-demo only
      const expired = companies.filter(c =>
        !c.is_demo &&
        c.subscription_status === 'trialing' &&
        c.trial_ends_at &&
        new Date(c.trial_ends_at) < now
      );
      setExpiredTrials(expired);

      // Inactive 30+ days (no recent activity — approximate by created_at for companies that aren't active)
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      const inactive = companies.filter(c =>
        c.subscription_status !== 'active' &&
        c.subscription_status !== 'trialing' &&
        new Date(c.created_at) < thirtyDaysAgo
      );
      setInactiveCompanies(inactive);

      // Active trials — anyone using the app without a real stripe subscription,
      // not counting demo/sandbox playgrounds.
      const isOpenTrial = (c) =>
        !c.is_demo &&
        !c.stripe_subscription_id &&
        (c.subscription_status === 'trialing' || c.subscription_status === 'active');

      const activeTrials = companies.filter(isOpenTrial);
      setActiveTrialsCount(activeTrials.length);

      // Trials over 14 days old — the "free riders". They've had the standard
      // trial window and haven't paid. Sorted oldest-first so highest priority
      // for follow-up sits at the top.
      const fourteenDaysAgoMs = Date.now() - 14 * 24 * 60 * 60 * 1000;
      const stale = activeTrials
        .filter(c => new Date(c.created_at).getTime() < fourteenDaysAgoMs)
        .sort((a, b) => new Date(a.created_at) - new Date(b.created_at))
        .map(c => ({
          ...c,
          owner: ownerMap[c.id] || null,
          ageDays: Math.floor((Date.now() - new Date(c.created_at).getTime()) / (1000 * 60 * 60 * 24)),
        }));
      setOldTrialsCount(stale.length);
      setOldTrials(stale);

    } catch (error) {
      console.error('Error loading overview:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: '3rem', textAlign: 'center', color: '#888' }}>
        Loading overview...
      </div>
    );
  }

  const getTrendInfo = (current, previous) => {
    if (current > previous) return { direction: 'up', color: '#4ecca3' };
    if (current < previous) return { direction: 'down', color: '#e74c3c' };
    return { direction: 'flat', color: '#666' };
  };

  const mrrDelta = stats.mrr - trends.prevMrr;
  const mrrPctChange = trends.prevMrr > 0 ? ((mrrDelta / trends.prevMrr) * 100) : (stats.mrr > 0 ? 100 : 0);

  const statCards = [
    {
      label: 'Total Companies',
      value: stats.totalCompanies.toLocaleString(),
      icon: Building2,
      color: '#3498db',
      newThisWeek: trends.newCompaniesThisWeek,
      trend: getTrendInfo(trends.newCompaniesThisWeek, trends.newCompaniesPrevWeek)
    },
    {
      label: 'Total Users',
      value: stats.totalUsers.toLocaleString(),
      icon: Users,
      color: '#9b59b6',
      newThisWeek: trends.newUsersThisWeek,
      trend: getTrendInfo(trends.newUsersThisWeek, trends.newUsersPrevWeek)
    },
    {
      label: 'Active Subscriptions',
      value: stats.activeSubscriptions.toLocaleString(),
      icon: CreditCard,
      color: '#4ecca3',
      newThisWeek: trends.newSubsThisWeek,
      trend: getTrendInfo(trends.newSubsThisWeek, trends.newSubsPrevWeek)
    },
    {
      label: 'Active Trials',
      value: activeTrialsCount.toLocaleString(),
      icon: Clock,
      color: '#f7931e',
      newThisWeek: null,
      trend: { direction: 'flat', color: '#666' },
      sub: 'In trial, not yet paying'
    },
    {
      label: 'Trials > 14 Days',
      value: oldTrialsCount.toLocaleString(),
      icon: AlertTriangle,
      color: oldTrialsCount > 0 ? '#e74c3c' : '#666',
      newThisWeek: null,
      trend: { direction: 'flat', color: '#666' },
      sub: 'Past standard trial window'
    },
    {
      label: 'MRR Estimate',
      value: formatCurrency(stats.mrr),
      icon: DollarSign,
      color: '#ff6b35',
      newThisWeek: null,
      mrrDelta,
      mrrPctChange,
      trend: getTrendInfo(stats.mrr, trends.prevMrr)
    }
  ];

  return (
    <div style={{ padding: '2rem 3rem' }}>
      {/* Header */}
      <div style={{ marginBottom: '2rem' }}>
        <h1 style={{
          fontSize: '2rem',
          fontWeight: '700',
          color: '#e0e0e0',
          marginBottom: '0.5rem'
        }}>
          Platform Overview
        </h1>
        <p style={{ color: '#888' }}>
          Work Van platform health and activity at a glance
        </p>
      </div>

      {/* Stats Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
        gap: '1.5rem',
        marginBottom: '2rem'
      }}>
        {statCards.map((stat, idx) => {
          const Icon = stat.icon;
          const TrendIcon = stat.trend.direction === 'up' ? TrendingUp
            : stat.trend.direction === 'down' ? TrendingDown : Minus;
          return (
            <div
              key={idx}
              style={{
                background: '#1a1a1a',
                borderRadius: '12px',
                padding: '1.5rem',
                border: '1px solid #2a2a2a',
                borderLeft: `4px solid ${stat.color}`
              }}
            >
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: '1rem'
              }}>
                <Icon size={24} color={stat.color} />
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.25rem'
                }}>
                  <TrendIcon size={14} color={stat.trend.color} />
                  {stat.newThisWeek !== null && stat.newThisWeek !== undefined ? (
                    <span style={{
                      fontSize: '0.75rem',
                      color: stat.trend.color,
                      fontWeight: '600'
                    }}>
                      +{stat.newThisWeek} this week
                    </span>
                  ) : stat.mrrDelta !== undefined ? (
                    <span style={{
                      fontSize: '0.75rem',
                      color: stat.trend.color,
                      fontWeight: '600'
                    }}>
                      {mrrDelta >= 0 ? '+' : ''}{formatCurrency(stat.mrrDelta)}
                    </span>
                  ) : null}
                </div>
              </div>
              <div style={{
                fontSize: '2rem',
                fontWeight: '700',
                color: stat.color,
                fontFamily: '"JetBrains Mono", monospace',
                marginBottom: '0.25rem'
              }}>
                {stat.value}
              </div>
              <div style={{
                fontSize: '0.9rem',
                color: '#e0e0e0',
                fontWeight: '500'
              }}>
                {stat.label}
              </div>
              {stat.sub && (
                <div style={{
                  fontSize: '0.75rem',
                  color: '#888',
                  marginTop: '0.25rem'
                }}>
                  {stat.sub}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Trial Conversion Funnel & MRR Trend */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
        gap: '1.5rem',
        marginBottom: '2rem'
      }}>
        {/* Trial Conversion Funnel */}
        <div style={{
          background: '#1a1a1a',
          borderRadius: '12px',
          border: '1px solid #2a2a2a',
          padding: '1.5rem'
        }}>
          <h3 style={{
            fontSize: '0.95rem',
            fontWeight: '700',
            color: '#e0e0e0',
            marginBottom: '1.25rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}>
            <CreditCard size={16} color="#4ecca3" />
            Trial Conversion (Last 30 Days)
          </h3>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {/* Trials Started */}
            <div>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                marginBottom: '0.35rem'
              }}>
                <span style={{ fontSize: '0.8rem', color: '#888' }}>Trials Started</span>
                <span style={{
                  fontSize: '0.85rem',
                  color: '#e0e0e0',
                  fontWeight: '600',
                  fontFamily: '"JetBrains Mono", monospace'
                }}>
                  {trialFunnel.trialsStarted30d}
                </span>
              </div>
              <div style={{
                height: '6px',
                background: '#2a2a2a',
                borderRadius: '3px',
                overflow: 'hidden'
              }}>
                <div style={{
                  height: '100%',
                  width: '100%',
                  background: '#3498db',
                  borderRadius: '3px'
                }} />
              </div>
            </div>

            {/* Converted */}
            <div>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                marginBottom: '0.35rem'
              }}>
                <span style={{ fontSize: '0.8rem', color: '#888' }}>Converted to Active</span>
                <span style={{
                  fontSize: '0.85rem',
                  color: '#e0e0e0',
                  fontWeight: '600',
                  fontFamily: '"JetBrains Mono", monospace'
                }}>
                  {trialFunnel.converted30d}
                </span>
              </div>
              <div style={{
                height: '6px',
                background: '#2a2a2a',
                borderRadius: '3px',
                overflow: 'hidden'
              }}>
                <div style={{
                  height: '100%',
                  width: trialFunnel.trialsStarted30d > 0
                    ? `${(trialFunnel.converted30d / trialFunnel.trialsStarted30d) * 100}%`
                    : '0%',
                  background: '#4ecca3',
                  borderRadius: '3px',
                  transition: 'width 0.5s ease'
                }} />
              </div>
            </div>

            {/* Conversion Rate */}
            <div style={{
              display: 'flex',
              alignItems: 'baseline',
              gap: '0.5rem',
              paddingTop: '0.25rem',
              borderTop: '1px solid #2a2a2a'
            }}>
              <span style={{
                fontSize: '1.5rem',
                fontWeight: '700',
                color: trialFunnel.conversionRate >= 50 ? '#4ecca3' : trialFunnel.conversionRate >= 25 ? '#f7931e' : '#e74c3c',
                fontFamily: '"JetBrains Mono", monospace'
              }}>
                {trialFunnel.conversionRate.toFixed(1)}%
              </span>
              <span style={{ fontSize: '0.8rem', color: '#888' }}>conversion rate</span>
            </div>
          </div>
        </div>

        {/* MRR Trend */}
        <div style={{
          background: '#1a1a1a',
          borderRadius: '12px',
          border: '1px solid #2a2a2a',
          padding: '1.5rem'
        }}>
          <h3 style={{
            fontSize: '0.95rem',
            fontWeight: '700',
            color: '#e0e0e0',
            marginBottom: '1.25rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}>
            <DollarSign size={16} color="#ff6b35" />
            MRR Trend
          </h3>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {/* Current vs Previous */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-end'
            }}>
              <div>
                <div style={{ fontSize: '0.8rem', color: '#888', marginBottom: '0.25rem' }}>
                  Current MRR
                </div>
                <div style={{
                  fontSize: '1.5rem',
                  fontWeight: '700',
                  color: '#ff6b35',
                  fontFamily: '"JetBrains Mono", monospace'
                }}>
                  {formatCurrency(stats.mrr)}
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: '0.8rem', color: '#888', marginBottom: '0.25rem' }}>
                  Est. Last Month
                </div>
                <div style={{
                  fontSize: '1.1rem',
                  fontWeight: '600',
                  color: '#888',
                  fontFamily: '"JetBrains Mono", monospace'
                }}>
                  {formatCurrency(trends.prevMrr)}
                </div>
              </div>
            </div>

            {/* Delta bar */}
            <div style={{
              padding: '0.75rem',
              background: mrrDelta >= 0 ? '#4ecca310' : '#e74c3c10',
              borderRadius: '8px',
              border: `1px solid ${mrrDelta >= 0 ? '#4ecca320' : '#e74c3c20'}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}>
                {mrrDelta >= 0
                  ? <TrendingUp size={16} color="#4ecca3" />
                  : <TrendingDown size={16} color="#e74c3c" />
                }
                <span style={{
                  fontSize: '0.9rem',
                  fontWeight: '600',
                  color: mrrDelta >= 0 ? '#4ecca3' : '#e74c3c',
                  fontFamily: '"JetBrains Mono", monospace'
                }}>
                  {mrrDelta >= 0 ? '+' : ''}{formatCurrency(mrrDelta)}
                </span>
              </div>
              <span style={{
                fontSize: '0.85rem',
                fontWeight: '600',
                color: mrrDelta >= 0 ? '#4ecca3' : '#e74c3c',
                fontFamily: '"JetBrains Mono", monospace'
              }}>
                {mrrPctChange >= 0 ? '+' : ''}{mrrPctChange.toFixed(1)}%
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Trials Older Than 14 Days — the "free riders" */}
      {oldTrials.length > 0 && (
        <div style={{
          background: '#1a1a1a',
          borderRadius: '12px',
          border: '1px solid #e74c3c40',
          padding: '1.25rem',
          marginBottom: '2rem'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: '0.75rem',
            flexWrap: 'wrap',
            gap: '0.5rem'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <AlertTriangle size={18} color="#e74c3c" />
              <span style={{ color: '#e74c3c', fontWeight: '600', fontSize: '0.95rem' }}>
                Trials over 14 days old ({oldTrials.length})
              </span>
            </div>
            <span style={{ color: '#888', fontSize: '0.8rem' }}>
              Active accounts past the standard trial window without a paid subscription
            </span>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'minmax(160px, 2fr) minmax(140px, 2fr) 1fr 1fr 0.5fr',
            gap: '0.5rem 1rem',
            fontSize: '0.8rem',
            padding: '0.5rem 0',
            borderBottom: '1px solid #2a2a2a',
            color: '#888',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.04em',
          }}>
            <div>Company</div>
            <div>Owner</div>
            <div>Tier</div>
            <div>Status</div>
            <div style={{ textAlign: 'right' }}>Age</div>
          </div>

          {oldTrials.slice(0, 15).map(c => (
            <div
              key={c.id}
              onClick={() => navigateToCompany && navigateToCompany(c.id)}
              style={{
                display: 'grid',
                gridTemplateColumns: 'minmax(160px, 2fr) minmax(140px, 2fr) 1fr 1fr 0.5fr',
                gap: '0.5rem 1rem',
                fontSize: '0.85rem',
                padding: '0.6rem 0',
                borderBottom: '1px solid #1f1f1f',
                color: '#e0e0e0',
                cursor: navigateToCompany ? 'pointer' : 'default',
              }}
            >
              <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {c.name}
              </div>
              <div style={{ color: '#aaa', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {c.owner?.full_name || c.owner?.email || '—'}
              </div>
              <div style={{ color: '#aaa', textTransform: 'capitalize' }}>{c.tier || '—'}</div>
              <div style={{ color: '#aaa' }}>{c.subscription_status}</div>
              <div style={{ textAlign: 'right', color: c.ageDays >= 30 ? '#e74c3c' : '#f39c12', fontWeight: 600 }}>
                {c.ageDays}d
              </div>
            </div>
          ))}

          {oldTrials.length > 15 && (
            <div style={{ marginTop: '0.5rem', textAlign: 'center', color: '#666', fontSize: '0.8rem' }}>
              + {oldTrials.length - 15} more
            </div>
          )}
        </div>
      )}

      {/* Quick Health */}
      {(expiredTrials.length > 0 || inactiveCompanies.length > 0) && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: '1.5rem',
          marginBottom: '2rem'
        }}>
          {expiredTrials.length > 0 && (
            <div style={{
              background: '#1a1a1a',
              borderRadius: '12px',
              border: '1px solid #f39c1240',
              padding: '1.25rem'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: '0.75rem',
                flexWrap: 'wrap',
                gap: '0.5rem'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <AlertTriangle size={18} color="#f39c12" />
                  <span style={{ color: '#f39c12', fontWeight: '600', fontSize: '0.9rem' }}>
                    Expired Trials ({expiredTrials.length})
                  </span>
                </div>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                  <button
                    onClick={() => {
                      if (selectedTrials.size === expiredTrials.length) {
                        setSelectedTrials(new Set());
                      } else {
                        setSelectedTrials(new Set(expiredTrials.map(c => c.id)));
                      }
                    }}
                    style={{
                      padding: '0.3rem 0.6rem', background: '#2a2a2a', border: 'none',
                      borderRadius: '4px', color: '#888', cursor: 'pointer', fontSize: '0.75rem'
                    }}
                  >
                    {selectedTrials.size === expiredTrials.length ? 'Deselect All' : 'Select All'}
                  </button>
                  {selectedTrials.size > 0 && (
                    <button
                      disabled={bulkLoading}
                      onClick={async () => {
                        const days = window.prompt(`Extend trial by how many days? (7 or 14)`, '14');
                        if (!days || isNaN(parseInt(days))) return;
                        const daysNum = parseInt(days);
                        setBulkLoading(true);
                        const newEnd = new Date(Date.now() + daysNum * 24 * 60 * 60 * 1000).toISOString();
                        const ids = [...selectedTrials];
                        try {
                          const { error } = await supabase
                            .from('companies')
                            .update({ trial_ends_at: newEnd })
                            .in('id', ids);
                          if (error) throw error;
                          const adminEmail = (() => { try { return JSON.parse(localStorage.getItem('workvan_internal_auth'))?.email; } catch { return 'unknown'; } })();
                          await logAdminAction(adminEmail, 'bulk_trial_extension', 'company', null, `${ids.length} companies`, { days: daysNum, company_ids: ids });
                          alert(`Extended trial by ${daysNum} days for ${ids.length} companies`);
                          setSelectedTrials(new Set());
                          loadOverviewData();
                        } catch (err) {
                          alert('Failed: ' + err.message);
                        } finally {
                          setBulkLoading(false);
                        }
                      }}
                      style={{
                        padding: '0.3rem 0.6rem', background: '#ff6b35', border: 'none',
                        borderRadius: '4px', color: '#fff', cursor: bulkLoading ? 'wait' : 'pointer',
                        fontSize: '0.75rem', fontWeight: '600', opacity: bulkLoading ? 0.7 : 1
                      }}
                    >
                      Extend Trial ({selectedTrials.size})
                    </button>
                  )}
                </div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {expiredTrials.slice(0, 10).map(c => (
                  <div
                    key={c.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      padding: '0.5rem 0.75rem',
                      background: selectedTrials.has(c.id) ? '#2a2a1a' : '#242424',
                      borderRadius: '6px',
                      border: selectedTrials.has(c.id) ? '1px solid #f39c1240' : '1px solid transparent'
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={selectedTrials.has(c.id)}
                      onChange={(e) => {
                        const next = new Set(selectedTrials);
                        if (e.target.checked) next.add(c.id);
                        else next.delete(c.id);
                        setSelectedTrials(next);
                      }}
                      style={{ accentColor: '#ff6b35' }}
                    />
                    <span
                      onClick={() => navigateToCompany(c.id)}
                      style={{ color: '#e0e0e0', fontSize: '0.85rem', cursor: 'pointer', flex: 1 }}
                    >
                      {c.name}
                    </span>
                    <span style={{ color: '#888', fontSize: '0.8rem' }}>
                      expired {formatDate(c.trial_ends_at)}
                    </span>
                  </div>
                ))}
                {expiredTrials.length > 10 && (
                  <div style={{ color: '#888', fontSize: '0.8rem', textAlign: 'center', paddingTop: '0.25rem' }}>
                    +{expiredTrials.length - 10} more
                  </div>
                )}
              </div>
            </div>
          )}

          {inactiveCompanies.length > 0 && (
            <div style={{
              background: '#1a1a1a',
              borderRadius: '12px',
              border: '1px solid #e74c3c40',
              padding: '1.25rem'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                marginBottom: '0.75rem'
              }}>
                <Clock size={18} color="#e74c3c" />
                <span style={{ color: '#e74c3c', fontWeight: '600', fontSize: '0.9rem' }}>
                  Inactive 30+ Days ({inactiveCompanies.length})
                </span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {inactiveCompanies.slice(0, 5).map(c => (
                  <div
                    key={c.id}
                    onClick={() => navigateToCompany(c.id)}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      padding: '0.5rem 0.75rem',
                      background: '#242424',
                      borderRadius: '6px',
                      cursor: 'pointer'
                    }}
                  >
                    <span style={{ color: '#e0e0e0', fontSize: '0.85rem' }}>{c.name}</span>
                    <span style={{ color: '#888', fontSize: '0.8rem', textTransform: 'capitalize' }}>
                      {c.subscription_status}
                    </span>
                  </div>
                ))}
                {inactiveCompanies.length > 5 && (
                  <div style={{ color: '#888', fontSize: '0.8rem', textAlign: 'center', paddingTop: '0.25rem' }}>
                    +{inactiveCompanies.length - 5} more
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Recent Activity */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
        gap: '1.5rem'
      }}>
        {/* Recent Companies */}
        <div style={{
          background: '#1a1a1a',
          borderRadius: '12px',
          border: '1px solid #2a2a2a',
          padding: '1.5rem'
        }}>
          <h2 style={{
            fontSize: '1.1rem',
            fontWeight: '700',
            color: '#e0e0e0',
            marginBottom: '1rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}>
            <Building2 size={18} color="#3498db" />
            Recent Companies
          </h2>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {recentCompanies.map(company => (
              <div
                key={company.id}
                onClick={() => navigateToCompany(company.id)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '0.75rem',
                  background: '#242424',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  transition: 'background 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = '#2a2a2a'}
                onMouseLeave={(e) => e.currentTarget.style.background = '#242424'}
              >
                <div>
                  <div style={{ fontWeight: '600', color: '#e0e0e0', fontSize: '0.9rem' }}>
                    {company.name}
                  </div>
                  <div style={{ fontSize: '0.8rem', color: '#888' }}>
                    {company.owner ? company.owner.full_name || company.owner.email : 'No owner'}
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{
                    padding: '0.2rem 0.5rem',
                    background: company.subscription_status === 'active' ? '#4ecca320' :
                      company.subscription_status === 'trialing' ? '#f7931e20' : '#e74c3c20',
                    color: company.subscription_status === 'active' ? '#4ecca3' :
                      company.subscription_status === 'trialing' ? '#f7931e' : '#e74c3c',
                    borderRadius: '4px',
                    fontSize: '0.7rem',
                    fontWeight: '600',
                    textTransform: 'capitalize'
                  }}>
                    {company.subscription_status}
                  </div>
                  <div style={{ fontSize: '0.75rem', color: '#666', marginTop: '0.25rem' }}>
                    {formatDate(company.created_at)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Recent Users */}
        <div style={{
          background: '#1a1a1a',
          borderRadius: '12px',
          border: '1px solid #2a2a2a',
          padding: '1.5rem'
        }}>
          <h2 style={{
            fontSize: '1.1rem',
            fontWeight: '700',
            color: '#e0e0e0',
            marginBottom: '1rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}>
            <Users size={18} color="#9b59b6" />
            Recent Signups
          </h2>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {recentUsers.map(user => (
              <div
                key={user.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '0.75rem',
                  background: '#242424',
                  borderRadius: '8px'
                }}
              >
                <div>
                  <div style={{ fontWeight: '600', color: '#e0e0e0', fontSize: '0.9rem' }}>
                    {user.full_name || 'Unnamed'}
                  </div>
                  <div style={{ fontSize: '0.8rem', color: '#888' }}>
                    {user.email}
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  {user.company ? (
                    <div
                      onClick={() => navigateToCompany(user.company.id)}
                      style={{
                        color: '#3498db',
                        fontSize: '0.8rem',
                        cursor: 'pointer',
                        fontWeight: '500'
                      }}
                    >
                      {user.company.name}
                    </div>
                  ) : (
                    <div style={{ color: '#666', fontSize: '0.8rem' }}>No company</div>
                  )}
                  <div style={{ fontSize: '0.75rem', color: '#666', marginTop: '0.25rem' }}>
                    {formatDate(user.created_at)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
